import { CommonModule } from '@angular/common';
import { 
  ChangeDetectionStrategy, 
  Component, 
  OnDestroy, 
  OnInit
} from '@angular/core';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import { BreadcrumbService } from '@shared/components/breadcrumb/breadcrumb.service';
import { FiledownloadService } from '../service/filedownload.service';
import { 
  EMPTY, 
  from, 
  Subject, 
  switchMap, 
  takeUntil, 
  tap 
} from 'rxjs';
import { SignalsService } from '@shared/services/signals/signals.service';
import { AppService } from 'app/app.service';
import { DownloadBlob } from './model/downloadBlob';
import { FormatDateTimePipe } from '@shared/pipes/format-time.pipe';
import { DateTime } from 'luxon';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { AccordionModule } from 'primeng/accordion';
import { fadeOpenClose } from '@shared/animations/fadeOpenClose.animation';

@Component({
    selector: 'app-download',
    imports: [
        CommonModule,
        BreadcrumbComponent,
        FormatDateTimePipe,
        ConfirmPopupModule,
        AccordionModule
    ],
    animations: [fadeOpenClose],
    templateUrl: './download.component.html',
    styleUrl: './download.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DownloadComponent implements OnInit, OnDestroy {

  theme$ = this.appService.theme$;
  destroy$ = new Subject<void>();
  csvFileSubject = new Subject<DownloadBlob[]>();
  mapFilesSubject = new Subject<DownloadBlob[]>();
  csvFiles$ = this.csvFileSubject.asObservable();
  mapFiles$ = this.mapFilesSubject.asObservable();

  isEditing: boolean = false;

  constructor(
    private breadcrumbService: BreadcrumbService,
    private downloadService: FiledownloadService,
    private signalService: SignalsService,
    public appService: AppService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
  ) {}

  ngOnInit(): void {
    const url = location.pathname;
    const breadcrumbLabel = 'Downloads';
    this.breadcrumbService.addToBreadcrumbs(url, breadcrumbLabel);

    this.getCsvFiles(); // Fetch all files
    this.getAllMapFiles(); // Fetch all map files
  }

  getCsvFiles() {
    return this.signalService.employeeNumber$.pipe(
      takeUntil(this.destroy$),
      switchMap((empNum) => {
        if (!empNum) {
          console.error('Employee number is undefined or null');
          return EMPTY;
        }

        return from(this.downloadService.getAllExportFiles()).pipe(
          tap((blob) => {
            if (!blob || blob.length === 0) {
              this.csvFiles$ = EMPTY;
            }
            this.csvFileSubject.next(blob as DownloadBlob[]);
          })
        );
      })
    ).subscribe();
  }

  getAllMapFiles() {
    return this.signalService.employeeNumber$.pipe(
      takeUntil(this.destroy$),
      switchMap((empNum) => {
        if (!empNum) {
          console.error('Employee number is undefined or null');
          return EMPTY;
        }

        return from(this.downloadService.getAllMapFiles()).pipe(
          tap((blob) => {
            if (!blob || blob.length === 0) {
              this.mapFiles$ = EMPTY;
            }
            this.mapFilesSubject.next(blob as DownloadBlob[]);
          })
        );
      })
    ).subscribe();
  }

  async downloadBlob(blob: DownloadBlob): Promise<void> {
    if (!blob) return;

    this.downloadService.lastDownloadTime = DateTime.now().toISO();
    const isMobile = this.isMobileView();

    const link = document.createElement('a');
    link.style.display = 'none';
    link.download = blob.fileName;

    if (isMobile) {
      let mobileUrl = await this.formatMobileDownloadLink(blob.blobName, blob.fileName);
      link.href = mobileUrl;
    } else {
      const url = window.URL.createObjectURL(blob.blobName);
      link.href = url;
    }

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    link.remove();
    // Clean up object URL if not mobile
    if (!isMobile) {
      setTimeout(() => window.URL.revokeObjectURL(link.href), 1000);
    }
  }


  /**
   * Generates a formatted mobile download link for a given Blob and file name.
   *
   * This method encodes the file name, converts the Blob to a base64 string,
   * modifies the data URI prefix to use "video/mpeg", and constructs a download URL
   * suitable for mobile devices. The generated URL can be used to initiate a download
   * of the Blob content with the specified file name.
   *
   * @param blob - The Blob object representing the file to be downloaded.
   * @param fileName - The desired name for the downloaded file.
   * @returns A Promise that resolves to a formatted download URL as a string.
   */
  async formatMobileDownloadLink(blob: Blob, fileName: string): Promise<string> {
    const encodedFileName = encodeURIComponent(fileName);
    const base64Blob = await this.blobToBase64(blob);
    const prefixIndex = base64Blob.indexOf(";base64") + ";base64".length;
    const prefix = base64Blob.substring(0, prefixIndex);
    const downloadPrefix = "data:video/mpeg;base64";
    const finalDownloadPrefix = ';' + downloadPrefix;

    return `https://sentinel.org/download/${encodedFileName}` + '/' + base64Blob.replace(prefix, finalDownloadPrefix);
  }

  blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onloadend = () => {
        const base64data = reader.result as string;
        resolve(base64data);
      };

      reader.onerror = reject;
      reader.readAsDataURL(blob); // This returns a base64 string with MIME prefix
    });
  }

  isMobileView(): boolean {
    const ua = navigator.userAgent;
    return /android|iPad|iPhone|iPod|Mobile/i.test(ua);
  }

  onDelete(fileName: string): void {
    this.downloadService.deleteBlob(fileName).then(() => {
      this.getCsvFiles(); // Refresh the file list after deletion
      this.getAllMapFiles(); // Refresh the JSON file
      this.isEditing = false;
    })
      .catch(err => {
        console.error('Error deleting blob:', err);
      })
  }

  onDeleteConfirm(event: Event, fileName: string): void {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: `Are you sure you want to delete this file?
      Only 2 requests are allowed per hour for all logged-in devices.`,
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
      this.onDelete(fileName);
      this.messageService.add({ severity: 'success', summary: 'Confirmed', detail: 'File deleted', life: 3000 });
      },
      reject: () => {
      this.isEditing = false;
      }
    });

  }

  onEdit(): void {
    this.isEditing = !this.isEditing;
  }

  formatBytes(bytes: number, decimals: number = 1): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const size = parseFloat((bytes / Math.pow(k, i)).toFixed(dm));

    return `${size} ${sizes[i]}`;
  }

  downloadsAccordion = {
    header: {
      color: '{text.color}',
      padding: '0.75rem 1rem',
      fontWeight: '400'
    },
    content: {
      background: 'transparent',
      padding: '0',
    },
    colorScheme: {
      light: {
        header: {
          background: '{surface.0}',
          hoverBackground: '{surface.0}',
          activeBackground: '{surface.0}',
          activeHoverBackground: '{surface.0}',
        }
      },
      dark: {
        header: {
          background: '{surface.50}',
          hoverBackground: '{surface.50}',
          activeBackground: '{surface.50}',
          activeHoverBackground: '{surface.50}',
        }
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

}